/**
 * Fixed louvred wall vent for the fattening hall.
 *
 * Its origin is the bottom edge, centred in X/Z, with the face toward +Z. The
 * asset deliberately carries no placement transform: the world positions and
 * turns it, so this same unit can sit on either wall without a mirrored variant.
 */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone, type ZonePalette } from '@/render/palette';

const zone: ZonePalette = (() => {
  const found = getZone('fattening');
  if (!found) throw new Error('fattening zone missing from the palette');
  return found;
})();

function colour(index: number): number {
  const value = zone.surfaces[index];
  if (value === undefined) throw new Error(`fattening palette surface ${String(index)} missing`);
  return value;
}

function addFrameBars(group: THREE.Group, material: THREE.Material, depth: number, z: number, prefix: string): void {
  for (const [x, y, width, height, name] of [
    [0, 0.89, 0.92, 0.06, 'top'],
    [0, 0.03, 0.92, 0.06, 'bottom'],
    [-0.43, 0.46, 0.06, 0.8, 'left'],
    [0.43, 0.46, 0.06, 0.8, 'right'],
  ] as const) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material);
    bar.name = `${prefix}-${name}`;
    bar.position.set(x, y, z);
    bar.castShadow = true;
    group.add(bar);
  }
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'wall-vent';

  const frameMaterial = surface('metal', colour(2));
  const bladeMaterial = surface('metal', colour(3));
  const darkMaterial = surface('metal', zone.shadow);
  addFrameBars(group, frameMaterial, 0.09, 0, 'mounting-frame');
  // This front lip is only 15 mm deep, held inside the 90 mm total depth. It
  // catches sodium light as a flange without quietly invalidating the kit's
  // placement footprint.
  addFrameBars(group, frameMaterial, 0.015, 0.0375, 'face-flange');

  const inset = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.8, 0.006), darkMaterial);
  inset.name = 'recessed-dark-inset';
  inset.position.set(0, 0.46, -0.04);
  inset.castShadow = true;
  group.add(inset);

  // Kept as a named pivot even while this beat leaves it shut. Any later system
  // rotates only this local X axis; the frame and the seven visible louvres stay
  // structurally fixed to the wall.
  const flap = new THREE.Group();
  flap.name = 'flap';
  flap.position.set(0, 0.46, -0.032);
  const flapPanel = new THREE.Mesh(new THREE.BoxGeometry(0.84, 0.84, 0.01), darkMaterial);
  flapPanel.name = 'flap-panel';
  flapPanel.castShadow = true;
  flap.add(flapPanel);
  group.add(flap);

  for (let index = 0; index < 7; index += 1) {
    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.86, 0.115, 0.022), bladeMaterial);
    blade.name = `louvre-blade-${index + 1}`;
    blade.position.set(0, 0.1 + index * 0.12, 0);
    blade.rotation.x = -Math.PI / 6;
    blade.castShadow = true;
    group.add(blade);
  }

  return group;
}

export interface WallVentParts {
  /** Closed at zero; rotate this local X pivot if the vent later needs to open. */
  flap: THREE.Object3D;
}

function required(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) throw new Error(`wall-vent: rig node "${name}" missing — was the mesh renamed?`);
  return found;
}

export function findWallVentParts(root: THREE.Object3D): WallVentParts {
  return { flap: required(root, 'flap') };
}

export const wallVentAsset: AssetDefinition = {
  id: 'wall-vent',
  label: 'Wall vent',
  description: '0.92 m fixed seven-louvre wall vent with a named interior flap pivot.',
  footprint: { width: 0.92, height: 0.92, length: 0.09 },
  create: build,
};
